import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import { useId, useState, type ReactNode } from 'react';

interface CollapsibleCardProps {
  title: string;
  /** One line under the title, for what the section is. */
  description?: string;
  /** Shown at the right of the header - a status chip, a count, a warning. */
  meta?: ReactNode;
  /** Open on first render. Ignored when `expanded` is supplied. */
  defaultExpanded?: boolean;
  /**
   * Controlled open state, for a page that needs to force a section open -
   * when a save inside it fails, for instance, and the message is in there.
   */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  children: ReactNode;
}

/**
 * One section of a long page, behind a header you can fold away.
 *
 * The account page had two columns of cards and ran to several screens, with
 * the billing form alone twenty fields deep. Stacking the sections full width
 * and letting each fold means the page opens at a length somebody can take in,
 * and each section gets the whole width rather than two thirds of it.
 *
 * The body stays mounted when it is closed. A form that unmounted would throw
 * away whatever had been typed into it the moment somebody folded the section
 * to look at another one, which is the opposite of what folding is for.
 *
 * The header is a button inside a heading, which is the accordion pattern
 * screen readers expect: the heading is how somebody skims the page, the
 * button is how they open the bit they want.
 */
export function CollapsibleCard({
  title,
  description,
  meta,
  defaultExpanded = false,
  expanded,
  onExpandedChange,
  children,
}: CollapsibleCardProps) {
  const [uncontrolled, setUncontrolled] = useState(defaultExpanded);
  const bodyId = useId();
  const headerId = useId();

  const isOpen = expanded ?? uncontrolled;

  const toggle = () => {
    const next = !isOpen;
    setUncontrolled(next);
    onExpandedChange?.(next);
  };

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Typography component="h2" sx={{ m: 0 }}>
        <ButtonBase
          id={headerId}
          onClick={toggle}
          aria-expanded={isOpen}
          aria-controls={bodyId}
          sx={{
            width: '100%',
            px: 3,
            py: 2,
            gap: 2,
            display: 'flex',
            alignItems: 'center',
            textAlign: 'left',
            justifyContent: 'space-between',
            '&:focus-visible': { outline: '2px solid', outlineOffset: -2 },
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            {description ? (
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block' }}
              >
                {description}
              </Typography>
            ) : null}
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {meta}
            <ExpandMoreIcon
              sx={{
                color: 'text.secondary',
                transition: 'transform 150ms',
                transform: isOpen ? 'rotate(180deg)' : 'none',
              }}
            />
          </Box>
        </ButtonBase>
      </Typography>

      <Collapse in={isOpen}>
        <Box
          id={bodyId}
          role="region"
          aria-labelledby={headerId}
          sx={{ px: 3, pb: 3, pt: 0, borderTop: 1, borderColor: 'divider' }}
        >
          <Box sx={{ pt: 3 }}>{children}</Box>
        </Box>
      </Collapse>
    </Paper>
  );
}
